// Drawing the webtop: render() rebuilds the tiles from `webtop` (keeping widgets that ask to be kept, and a note
// being typed into), tileEl() builds one tile, and the selection is drawn here. Redraws on a real resize.
import { WIDGETS } from '../widgets/index.js';
import { editField } from '../widgets/kit.js';
import { applyBackground } from './background.js';
import { band, devicePx, drawnSize, EDGE, fitX, layout, placeTile, $root, safeArea, screenH, sizeKey, sizeOf, sizesPerScreen, unscaled } from './geometry.js';
import { drawGroupPanel, groupTile, openGroupId } from './groups.js';
import { hideMenu } from './menu.js';
import { notchAtBottom, rememberWebtop } from './notch.js';
import { addGrips } from './resize.js';
import { bus, currentRole, $webtop, webtop, selected } from './state.js';
import { iconNode, isGroup, needsAttention, onWebtop, targetText, tileIcon } from './tiles.js';

// Size, place and selection of a tile element: shared by a new element and a kept one (see render).
export function fitTile(el, s, p) {
  const size = drawnSize(s);
  el.classList.remove(...[...el.classList].filter((c) => c.startsWith('size-')));
  el.classList.add(`size-${sizeKey(s)}`);
  el.style.width = `${devicePx(size.w)}px`;
  el.style.height = `${devicePx(size.h)}px`;
  el.classList.toggle('selected', selected.has(s.id));
  placeTile(el, p);
  WIDGETS[s.kind]?.fit?.(s, el);
  addGrips(el, s);
}

export function tileEl(s, p) {
  const el = document.createElement('div');
  el.className = 'tile';
  el.tabIndex = 0;
  el.dataset.id = s.id;
  const widget = WIDGETS[s.kind];
  if (widget) { // a widget draws its own body
    el.classList.add('widget', s.kind, widget.look);
    if (widget.keep) el.dataset.keep = widget.keep(s);
    widget.render(s, el);
    fitTile(el, s, p);
    return el;
  }
  fitTile(el, s, p);
  if (isGroup(s)) return groupTile(s, el);
  el.setAttribute('role', 'link');
  el.title = !s.target ? 'No link set' : needsAttention(s) ? `${targetText(s)}: the applet is not installed or not enabled in this browser` : targetText(s);
  el.classList.toggle('attention', needsAttention(s));
  const title = document.createElement('div');
  title.className = 'title';
  title.textContent = s.title;
  el.append(iconNode(tileIcon(s), 'icon'), title);
  return el;
}

let refit = 0; // the pending redraw after the columns' width changed (relayout)
let drawnBand = null; // the screen size of the last render(), so a resize across one under Viewport redraws
export function render(first = []) {
  // Nothing to draw while no webtop is in (a locked one shows the lock screen): an applet registering a widget then
  // asks for a redraw too.
  if (!webtop) return;
  document.title = `${webtop.locked || webtop.owned ? (webtop.unlocked ? '🔓 ' : '🔒 ') : ''}${webtop.title} · webtop`;
  applyBackground();
  rememberWebtop();
  // A note being typed into survives the redraw: its caret and scroll are put back afterwards.
  const typing = document.activeElement?.closest?.('.tile.widget textarea');
  const keep = typing && { id: typing.closest('.tile').dataset.id, a: typing.selectionStart, b: typing.selectionEnd, top: typing.scrollTop };
  const scrollTop = scrollY; // emptying the webtop below would scroll the page back to the top
  $webtop.classList.remove('locked');
  $webtop.classList.toggle('viewer', currentRole() !== 'editor');
  // Widgets with `keep` (an embedded player) survive the redraw while their signature is unchanged: the same
  // element is moved and resized instead of rebuilt, since rebuilding an iframe reloads it and stops its music.
  const draw = () => {
    drawnBand = band();
    const kept = new Map([...$webtop.querySelectorAll('.tile[data-keep]')].map((el) => [el.dataset.id, el]));
    $webtop.querySelectorAll('.tile, .lock-screen, .scroll-end').forEach((e) => { if (!e.dataset.keep) e.remove(); });
    const pos = layout(first);
    for (const s of onWebtop()) {
      const old = kept.get(s.id);
      if (old && old.dataset.keep === WIDGETS[s.kind]?.keep?.(s)) {
        fitTile(old, s, pos.get(s.id));
        kept.delete(s.id);
      } else $webtop.appendChild(tileEl(s, pos.get(s.id)));
    }
    for (const el of kept.values()) el.remove(); // deleted, or changed so that it must be rebuilt
    scrollEnd(pos);
  };
  const width = $root.clientWidth;
  draw();
  if ($root.clientWidth !== width) draw(); // a scrollbar came or went and changed the width: lay out for the new one
  if (openGroupId) drawGroupPanel(); // before the scroll is put back: while it is open, the panel is what scrolls
  scrollTo(0, scrollTop);
  if (keep) {
    const ta = $webtop.querySelector(`.tile[data-id="${keep.id}"] textarea`);
    if (ta) { editField(ta); ta.setSelectionRange(keep.a, keep.b); ta.scrollTop = keep.top; }
  }
  $webtop.classList.toggle('empty', webtop.shortcuts.length === 0);
  bus.dispatchEvent(new CustomEvent('render')); // applets may follow redraws (client/lib/applets.js)
}

// A resize changes only where the tiles go, so the tiles already drawn are moved and resized in place instead of
// being rebuilt: rebuilding every tile (and its icon) on every resize event made dragging a window edge crawl on a
// webtop with hundreds of tiles. Anything that is not simply the drawn tiles (a lock screen, a tile missing) gets a
// full render(). Also run when a sidebar opens or closes (client/lib/views.js): the tiles keep clear of it.
export function relayout() {
  if (!webtop) return;
  // Tiles may change size across a screen size with the Viewport switch on, which a widget draws for: move on to a full render().
  if (sizesPerScreen() && band() !== drawnBand) return render();
  const els = new Map([...$webtop.querySelectorAll('.tile[data-id]')].map((el) => [el.dataset.id, el]));
  const tiles = onWebtop();
  if ($webtop.querySelector('.lock-screen') || tiles.length !== els.size || tiles.some((s) => !els.has(s.id))) return render();
  const scrollTop = scrollY;
  const fitBefore = fitX();
  const draw = () => {
    $webtop.querySelectorAll('.scroll-end').forEach((e) => e.remove());
    const pos = layout();
    for (const s of tiles) fitTile(els.get(s.id), s, pos.get(s.id));
    scrollEnd(pos);
  };
  const width = $root.clientWidth;
  draw();
  if ($root.clientWidth !== width) draw(); // a scrollbar came or went (see render)
  if (openGroupId) drawGroupPanel();
  scrollTo(0, scrollTop);
  // The columns' width changed: a built-in widget drew itself for the old one (an applet's follows in fitTile). Once the
  // resizing pauses, since rebuilding tiles on every frame of one crawls.
  if (fitX() !== fitBefore && tiles.some((s) => WIDGETS[s.kind] && s.kind !== 'applet')) {
    clearTimeout(refit);
    refit = setTimeout(render, 150);
  }
}

const NOTCH_GAP = 6; // how far above the notch the lowest tile can scroll
// When tiles reach below the screen, or one sits partly behind the notch, a marker past the lowest one makes the
// page scroll far enough to bring every tile out from behind the notch, which floats over them. Only at the bottom of the
// screen: scrolling further down brings nothing out from behind a notch moved anywhere else (client/lib/notch.js).
// #webtop is also made as tall as that marker (--webtop-h), since absolutely placed tiles do not stretch it: otherwise
// the empty space below the first screen would be the page's root, not #webtop, and a selection box could not
// start there.
function scrollEnd(pos) {
  $webtop.style.removeProperty('--webtop-h');
  const tiles = onWebtop();
  const bottom = Math.max(0, ...tiles.map((s) => pos.get(s.id).y + sizeOf(s).h));
  const screen = screenH();
  const n = unscaled(notchAtBottom() ? document.getElementById('notch').getBoundingClientRect() : new DOMRect());
  const behindNotch = n.width && tiles.some((s) => {
    const p = pos.get(s.id), size = sizeOf(s);
    return p.x * fitX() < n.right && (p.x + size.w) * fitX() > n.left && p.y + size.h > n.top - NOTCH_GAP;
  });
  if (bottom <= screen - EDGE - safeArea().bottom && !behindNotch) return;
  const end = Object.assign(document.createElement('div'), { className: 'scroll-end' });
  placeTile(end, { x: 0, y: bottom + (n.width ? screen - n.top + NOTCH_GAP : EDGE) });
  $webtop.appendChild(end);
  // Read from the style, not offsetTop, so drawing never forces a layout. The marker is 1 px (scaled) tall.
  $webtop.style.setProperty('--webtop-h', `${parseFloat(end.style.top) + devicePx(1)}px`);
}

export function setSelection(ids) {
  if (currentRole() !== 'editor') ids = []; // a locked webtop has no selection (nothing to move or delete)
  selected.clear();
  for (const id of ids) selected.add(id);
  $webtop.querySelectorAll('.tile').forEach((t) => t.classList.toggle('selected', selected.has(t.dataset.id)));
}
export const selectedShortcuts = () => webtop.shortcuts.filter((s) => selected.has(s.id));
// iOS Safari also fires resize while its toolbars collapse during a scroll; the layout only depends on the root
// element's size, so nothing is redrawn unless that changed. A window being resized fires many resize events a
// frame; they are drawn once, on the next animation frame.
let screenSize = ''; // the root element's size at the last layout, set by init
let resizing = 0; // the pending animation frame

// Event wiring and first drawing, run once by client.js in the order the parts of the page depend on.
export function init() {
  screenSize = `${$root.clientWidth}x${$root.clientHeight}`;
  addEventListener('resize', () => {
    if (resizing) return;
    resizing = requestAnimationFrame(() => {
      resizing = 0;
      const now = `${$root.clientWidth}x${$root.clientHeight}`;
      if (now === screenSize) return;
      screenSize = now;
      hideMenu();
      if (webtop) relayout();
    });
  });
}
